import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import mapaClientes from "@/assets/mapa-clientes.webp";

// Import logos
import logoElenice from "@/assets/logos/elenice.webp";
import logoOralix from "@/assets/logos/oralix.webp";
import logoLosFerrer from "@/assets/logos/los-ferrer.webp";
import logoHabito from "@/assets/logos/habito.webp";
import logoTrasLosPasos from "@/assets/logos/tras-los-pasos.webp";
import logoSheIs from "@/assets/logos/she-is-foundation.webp";
import logoDigix from "@/assets/logos/digix.webp";
import logoTransformacion from "@/assets/logos/transformacion-inmobiliaria.webp";
import logoStrow from "@/assets/logos/strow-agency.webp";
import logoBrindo from "@/assets/logos/brindo.webp";
import logoSergio from "@/assets/logos/sergio.webp";
import logoRatti from "@/assets/logos/ratti.webp";
import logoLasMisiones from "@/assets/logos/las-misiones.webp";
import logoSafeZone from "@/assets/logos/safe-zone.webp";
import logoCentro from "@/assets/logos/centro-pinturerias.webp";
import logoCebac from "@/assets/logos/cebac.webp";
import logoPuntomed from "@/assets/logos/puntomed.webp";
import logoMercurio from "@/assets/logos/mercurio.webp";
import logoDuomo from "@/assets/logos/duomo.webp";
import logoArevalo from "@/assets/logos/arevalo.webp";
import logoMedinor from "@/assets/logos/medinor.webp";

const stats = [{
  value: 13,
  suffix: "+",
  label: "Años"
}, {
  value: 1000,
  suffix: "+",
  label: "Proyectos"
}, {
  value: 20,
  suffix: "+",
  label: "Industrias"
}, {
  value: 15,
  suffix: "+",
  label: "Países"
}];

const clientLogos = [{
  src: logoDuomo,
  alt: "Duomo",
  invert: false
}, {
  src: logoMedinor,
  alt: "Medinor",
  invert: true
}, {
  src: logoMercurio,
  alt: "Grupo Mercurio",
  invert: false
}, {
  src: logoCebac,
  alt: "Cebac Laboratorios",
  invert: true
}, {
  src: logoCentro,
  alt: "Centro Pinturerías",
  invert: true
}, {
  src: logoDigix,
  alt: "Digix",
  invert: true
}, {
  src: logoRatti,
  alt: "Silvana Ratti",
  invert: false
}, {
  src: logoElenice,
  alt: "Elenice",
  invert: true
}, {
  src: logoOralix,
  alt: "Oralix",
  invert: true
}, {
  src: logoLosFerrer,
  alt: "Los Ferrer",
  invert: true
}, {
  src: logoHabito,
  alt: "Hábito Market",
  invert: true
}, {
  src: logoTrasLosPasos,
  alt: "Tras los Pasos",
  invert: true
}, {
  src: logoSheIs,
  alt: "She is Foundation",
  invert: true
}, {
  src: logoTransformacion,
  alt: "Transformación Inmobiliaria 360",
  invert: true
}, {
  src: logoStrow,
  alt: "Strow Agency",
  invert: true
}, {
  src: logoBrindo,
  alt: "Brindo",
  invert: false
}, {
  src: logoSergio,
  alt: "Sergio",
  invert: false
}, {
  src: logoLasMisiones,
  alt: "Las Misiones Droguería",
  invert: true
}, {
  src: logoSafeZone,
  alt: "Safe Zone Gaming Bar",
  invert: true
}, {
  src: logoPuntomed,
  alt: "Puntomed Consultorios Médicos",
  invert: true
}, {
  src: logoArevalo,
  alt: "Arévalo Construcciones",
  invert: true
}];

const AnimatedCounter = ({
  value,
  suffix,
  inView
}: {
  value: number;
  suffix: string;
  inView: boolean;
}) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value, inView]);
  return <span className="text-gradient-gold">
      {count}{suffix}
    </span>;
};

const Stats = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "-100px"
  });
  return <section className="py-24 md:py-32 relative overflow-hidden bg-black text-white" id="alcance">
      <div className="container px-4">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16 max-w-4xl mx-auto">
          <span className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block" style={{ color: "#7ab7fd" }}>Por qué Coressen</span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
            Trabajamos con instituciones
            <br />
            <span style={{ color: "#7ab7fd" }}>que tienen trayectoria real.</span>
          </h2>
          <div className="space-y-5 text-base md:text-lg text-white/75 leading-relaxed text-left md:text-center">
            <p>
              Trabajamos con instituciones que tienen trayectoria real y aspiraciones genuinas de crecimiento. No diseñamos logos. Construimos sistemas de marca que protegen el valor de lo que ya existe y habilitan lo que viene.
            </p>
            <p>
              En este proyecto específico, lo que Coressen aporta es algo que muy pocas consultoras pueden ofrecer: la capacidad de trabajar simultáneamente tres unidades de marca con coherencia estratégica entre ellas.
            </p>
          </div>
        </motion.div>

        {/* Stats grid */}
        <div ref={ref} className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className="text-center">
              <div className="font-display text-5xl md:text-6xl lg:text-7xl font-bold mb-2">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} inView={isInView} />
              </div>
              <p className="text-neutral-400 uppercase tracking-widest text-sm">
                {stat.label}
              </p>
            </motion.div>)}
        </div>

        {/* World map */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative rounded-2xl overflow-hidden mb-16">
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10" />
          <img src={mapaClientes} alt="Clientes en 15+ países" className="w-full h-auto opacity-80" />
        </motion.div>

        {/* Rotating logos */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative overflow-hidden">
          <div className="text-center mb-8">
            <p className="text-neutral-400 text-sm uppercase tracking-widest">
              Algunas marcas que confían en nosotros
            </p>
          </div>
          
          {/* Infinite scroll container */}
          <div className="relative">
            {/* Gradient masks */}
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-black to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-black to-transparent z-10" />
            
            {/* Scrolling logos */}
            <div className="flex animate-scroll-logos">
              {/* First set */}
              {clientLogos.map((logo, index) => <div key={`logo-1-${index}`} className="flex-shrink-0 mx-6 md:mx-10">
                  <div className="w-28 h-16 md:w-36 md:h-20 flex items-center justify-center">
                    <img src={logo.src} alt={logo.alt} className={`max-w-full max-h-full object-contain ${logo.invert ? 'invert brightness-0 invert' : ''}`} style={{
                  filter: logo.invert ? 'invert(1) brightness(2)' : 'none'
                }} />
                  </div>
                </div>)}
              {/* Duplicate set for seamless loop */}
              {clientLogos.map((logo, index) => <div key={`logo-2-${index}`} className="flex-shrink-0 mx-6 md:mx-10">
                  <div className="w-28 h-16 md:w-36 md:h-20 flex items-center justify-center">
                    <img src={logo.src} alt={logo.alt} className="max-w-full max-h-full object-contain" style={{
                  filter: logo.invert ? 'invert(1) brightness(2)' : 'none'
                }} />
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};

export default Stats;